import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, TextField, Typography } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import api from '../api/api';
import { formatPortalDate } from '../utils/dateFormat';
import { DefectReportDialog, InspectionReportDialog } from './TruckInspectionLogs';

const defectStatuses = {
  needs_attention: { label: 'Needs Attention', background: '#fff6e5', color: '#94610b' },
  fail: { label: 'Unsafe / Do Not Use', background: '#fff0f1', color: '#b42335' },
  pass: { label: 'Resolved / Informational', background: '#eaf6ef', color: '#267249' },
  resolved: { label: 'Resolved', background: '#e8f7ef', color: '#18734b' },
};

function isResolvedDefect(inspection) {
  return ['pass', 'resolved'].includes(inspection?.status) || inspection?.details?.defect_resolved === true;
}

export default function DashboardDefectReports() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(false);
  const resolvedOnPage = items.filter(({ inspection }) => isResolvedDefect(inspection)).length;
  const openOnPage = items.length - resolvedOnPage;

  useEffect(() => {
    const controller = new AbortController();
    let requestVersion = 0;
    const load = async (quiet = false) => {
      const version = ++requestVersion;
      if (!quiet) setLoading(true);
      try {
        const response = await api.get('/inspections', {
          signal: controller.signal,
          params: { type: 'defect_report', page, pageSize, q: search, status, sortBy: 'occurred_at', sortDir: 'desc' },
        });
        if (controller.signal.aborted || version !== requestVersion) return;
        setItems(response.data?.items || []);
        setTotal(Number(response.data?.total || 0));
        setError('');
        const lastPage = Math.max(0, Math.ceil(Number(response.data?.total || 0) / pageSize) - 1);
        if (page > lastPage) setPage(lastPage);
      } catch (exception) {
        if (!controller.signal.aborted && version === requestVersion) {
          setError(exception?.response?.data?.error || 'Unable to load reported defects.');
        }
      } finally {
        if (!controller.signal.aborted && version === requestVersion) setLoading(false);
      }
    };
    const timeout = window.setTimeout(() => load(), 250);
    const refresh = () => document.visibilityState === 'visible' && load(true);
    const interval = window.setInterval(refresh, 30_000);
    window.addEventListener('focus', refresh);
    window.addEventListener('dataUpdated', refresh);
    return () => {
      controller.abort();
      window.clearTimeout(timeout);
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener('dataUpdated', refresh);
    };
  }, [page, pageSize, search, status]);

  return (
    <Box sx={{ mt: 2, pt: 1.5, borderTop: '1px solid #e3eaf2' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap', mb: 1.25 }}>
        <Typography sx={{ fontWeight: 900, color: '#16324f' }}>Previously Reported Defects</Typography>
        <Box sx={{ display: 'flex', gap: .75, flexWrap: 'wrap' }}>
          <Chip size="small" label={`${openOnPage} open`} sx={{ bgcolor: '#fff3dc', color: '#8b5b08', fontWeight: 800 }} />
          <Chip size="small" label={`${resolvedOnPage} resolved`} sx={{ bgcolor: '#e8f7ef', color: '#18734b', fontWeight: 800 }} />
          <Chip size="small" label={`${total} reports`} sx={{ bgcolor: '#edf2f8', color: '#36536d', fontWeight: 800 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 1fr) minmax(0, 1fr)' }, gap: 1, mb: 1.25, p: 1.1, bgcolor: '#f6f9fc', borderRadius: 2, '& .MuiOutlinedInput-root': { bgcolor: 'white', borderRadius: 1.75 } }}>
        <TextField size="small" label="Search defects" placeholder="Vehicle, reporter or issue" value={search} onChange={(event) => { setSearch(event.target.value); setPage(0); }} />
        <TextField select size="small" label="Status" value={status} onChange={(event) => { setStatus(event.target.value); setPage(0); }}>
          <MenuItem value="">All statuses</MenuItem>
          {Object.entries(defectStatuses).map(([value, details]) => <MenuItem key={value} value={value}>{details.label}</MenuItem>)}
        </TextField>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
      {loading ? <Box sx={{ display: 'grid', placeItems: 'center', py: 3 }}><CircularProgress size={26} /></Box> : (
        <Box sx={{ display: 'grid', gap: 1, maxHeight: 340, overflowY: 'auto', pr: 0.5, scrollbarWidth: 'thin', scrollbarColor: '#cbd9e5 transparent' }}>
          {!items.length && !error && <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>No reported defects match these filters.</Typography>}
          {items.map(({ inspection, truck }) => {
            const badge = defectStatuses[inspection.status] || { label: inspection.status || 'Not specified', background: '#eef2f6', color: '#526273' };
            const resolved = isResolvedDefect(inspection);
            const resolvedAt = inspection.details?.defect_resolved_at;
            const resolvedBy = inspection.details?.defect_resolved_by_name;
            const hasResolutionAudit = Boolean(resolvedAt || resolvedBy || inspection.details?.defect_resolved);
            return (
              <Paper key={inspection.id} role="button" tabIndex={0} variant="outlined" onClick={() => setSelected(inspection)} onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); setSelected(inspection); } }} sx={{ p: 1.4, borderRadius: 2.25, cursor: 'pointer', background: resolved ? 'linear-gradient(135deg, #f3fbf7 0%, #ffffff 76%)' : 'white', borderColor: resolved ? '#b9dfca' : '#e0e7ef', borderLeft: `4px solid ${badge.color}99`, transition: 'background 160ms ease, box-shadow 160ms ease', '&:hover': { bgcolor: resolved ? '#edf9f2' : '#f7fafd', borderColor: resolved ? '#7fc39e' : '#bccfe1', boxShadow: '0 4px 12px rgba(16,42,67,.06)' }, '&:focus-visible': { outline: '2px solid #315f7d', outlineOffset: 2 } }}>
                <Box sx={{ display: 'flex', gap: 0.75, justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <Typography sx={{ fontWeight: 900, color: '#16324f' }}>{truck?.name || `Vehicle #${inspection.truck_id}`}{truck?.license_plate ? ` • ${truck.license_plate}` : ''}</Typography>
                  <Chip size="small" label={badge.label} sx={{ fontWeight: 800, bgcolor: badge.background, color: badge.color }} />
                </Box>
                <Typography variant="body2" sx={{ mt: 0.6, color: resolved ? '#547063' : '#475569', textDecoration: resolved ? 'line-through' : 'none', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>{inspection.issues || 'No defect description provided.'}</Typography>
                {resolved && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: .7, mt: 1, px: 1, py: .7, borderRadius: 1.5, bgcolor: '#e8f7ef', color: '#146a45' }}>
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                    <Typography variant="caption" sx={{ fontWeight: 800 }}>
                      {hasResolutionAudit ? 'Resolved' : 'Closed as informational'}{resolvedBy ? ` by ${resolvedBy}` : ''}{resolvedAt ? ` • ${formatPortalDate(resolvedAt, { includeTime: true })}` : ''}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: .75, flexWrap: 'wrap', mt: 1, pt: .8, borderTop: '1px solid #eef2f6' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: .75, flexWrap: 'wrap' }}>
                    <Box sx={{ px: 1, py: .5, borderRadius: 1.5, bgcolor: '#e8f0f8', border: '1px solid #cddded' }}>
                      <Typography sx={{ fontSize: { xs: '.88rem', sm: '.94rem' }, fontWeight: 900, color: '#163d63', lineHeight: 1.3 }}>
                        <Box component="span" sx={{ fontSize: '.65rem', textTransform: 'uppercase', letterSpacing: .55, color: '#526e87', mr: .65 }}>Reported by</Box>
                        {inspection.inspected_by || 'Reporter not recorded'}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ color: '#64748b' }}>{formatPortalDate(inspection.occurred_at, { includeTime: true })}</Typography>
                  </Box>
                  <Typography variant="caption" sx={{ color: '#315f7d', fontWeight: 800 }}>View report →</Typography>
                </Box>
              </Paper>
            );
          })}
        </Box>
      )}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1.25, p: 1, borderRadius: 2, bgcolor: '#f6f9fc' }}>
        <TextField select size="small" label="Per page" value={pageSize} onChange={(event) => { setPageSize(Number(event.target.value)); setPage(0); }} sx={{ width: 100 }}>{[25, 50, 100, 150].map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}</TextField>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Button size="small" disabled={loading || page === 0} onClick={() => setPage(page - 1)}>Previous</Button>
          <Typography variant="caption">{page + 1} / {Math.max(1, Math.ceil(total / pageSize))}</Typography>
          <Button size="small" disabled={loading || (page + 1) * pageSize >= total} onClick={() => setPage(page + 1)}>Next</Button>
        </Box>
      </Box>
      <InspectionReportDialog open={Boolean(selected) && !editing} inspection={selected} onClose={() => setSelected(null)} onEdit={() => setEditing(true)} />
      <DefectReportDialog open={Boolean(selected) && editing} truckId={selected?.truck_id} existingInspection={selected} onClose={() => { setEditing(false); setSelected(null); }} onSaved={() => { setEditing(false); setSelected(null); window.dispatchEvent(new Event('dataUpdated')); }} />
    </Box>
  );
}
